import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Loader2, Download, Mail, Eye, X, ChevronDown, History, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { clientDocumentsApi, type UnsurrenderedDocument, type AgeZone, type ClientDocument } from '@/api/client-documents';
import { clientDetailApi } from '@/api/client-detail';
import { reconciliationApi } from '@/api/reconciliation';
import { UnsurrenderedEmailDialog } from './components/UnsurrenderedEmailDialog';
import { ReprintDialog } from './components/ReprintDialog';
import { useCreateDocument } from '@/features/legacy/hooks/useDocuments';
import type { LegalEntity } from '@shared/types';

type ViewMode = 'unsurrendered' | 'all';

function getDocTypeLabel(type: 'CONTRACT' | 'AMENDMENT' | 'INVOICE' | 'ACT'): string {
  switch (type) {
    case 'CONTRACT': return 'Договор';
    case 'AMENDMENT': return 'Доп. соглашение';
    case 'INVOICE': return 'Счёт';
    case 'ACT': return 'Акт';
  }
}

function getReturnStatusLabel(status: string): string {
  switch (status) {
    case 'NOT_RETURNED': return 'Не сдан';
    case 'CLIENT_DELIVERED': return 'Отдан клиенту';
    case 'CLIENT_RETURNED': return 'Клиент вернул';
    case 'RETURNED': return 'Сдан в офис';
    case 'ARCHIVED': return 'В архиве';
    case 'NON_RETURNABLE': return 'Невозвратный';
    default: return status;
  }
}

function getReturnStatusColor(status: string): string {
  switch (status) {
    case 'NOT_RETURNED': return 'text-orange-600 dark:text-orange-400';
    case 'CLIENT_DELIVERED': return 'text-blue-600 dark:text-blue-400';
    case 'CLIENT_RETURNED': return 'text-cyan-600 dark:text-cyan-400';
    case 'RETURNED': return 'text-green-600 dark:text-green-400';
    case 'ARCHIVED': return 'text-gray-500 dark:text-gray-400';
    case 'NON_RETURNABLE': return 'text-violet-600 dark:text-violet-400';
    default: return 'text-muted-foreground';
  }
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getAgeZoneStyle(zone: AgeZone): string {
  switch (zone) {
    case 'normal': return 'text-muted-foreground';
    case 'danger': return 'text-amber-600 dark:text-amber-400 font-medium';
    case 'critical': return 'text-red-600 dark:text-red-400 font-medium';
    case 'extreme': return 'text-red-700 dark:text-red-300 font-bold underline';
  }
}

function getAgeZoneLabel(zone: AgeZone): string {
  switch (zone) {
    case 'normal': return '';
    case 'danger': return 'опасная зона';
    case 'critical': return 'критическая';
    case 'extreme': return 'крайне критическая';
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru-RU');
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear().toString().slice(-2)}`;
}

export function ClientDocumentsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clientIdNum = clientId ? parseInt(clientId, 10) : 0;

  // View mode: unsurrendered (default) or all documents
  const [viewMode, setViewMode] = useState<ViewMode>('unsurrendered');
  // Track which documents are being updated
  const [updatingDocs, setUpdatingDocs] = useState<Set<string>>(new Set());
  // Track selected documents
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Download in progress
  const [isDownloading, setIsDownloading] = useState(false);
  // Track which document is being previewed
  const [previewingDoc, setPreviewingDoc] = useState<string | null>(null);
  // PDF viewer state
  const [pdfViewerData, setPdfViewerData] = useState<{ url: string; title: string } | null>(null);
  // Reprint dialog state
  const [reprintDialogOpen, setReprintDialogOpen] = useState(false);

  // Create document mutation (for reprint requests)
  const createDocument = useCreateDocument();

  // Load client info
  const { data: clientDetail } = useQuery({
    queryKey: ['client-detail', clientIdNum],
    queryFn: () => clientDetailApi.getMobileDetail(clientIdNum),
    enabled: clientIdNum > 0,
  });

  const legalEntity = clientDetail?.client.legalEntity as LegalEntity | undefined;

  // Load available signers for legalEntity
  const { data: signers } = useQuery({
    queryKey: ['signers', legalEntity, new Date().toISOString().split('T')[0]],
    queryFn: () => reconciliationApi.getAvailableSigners(legalEntity!, new Date().toISOString().split('T')[0]),
    enabled: !!legalEntity,
  });

  // Use highest priority signer (first in the sorted list)
  const signerId = signers?.[0]?.id;

  // Load unsurrendered documents
  const { data: unsurrenderedData, isLoading: isLoadingUnsurrendered } = useQuery({
    queryKey: ['client-documents', clientIdNum],
    queryFn: () => clientDocumentsApi.getUnsurrenderedDocuments(clientIdNum),
    enabled: clientIdNum > 0,
  });

  // Load all documents (only when in 'all' mode)
  const { data: allDocsData, isLoading: isLoadingAllDocs } = useQuery({
    queryKey: ['client-all-documents', clientIdNum],
    queryFn: () => clientDocumentsApi.getAllDocuments(clientIdNum),
    enabled: clientIdNum > 0 && viewMode === 'all',
  });

  // Auto-switch to 'all' mode when all documents are surrendered
  useEffect(() => {
    if (unsurrenderedData && unsurrenderedData.summary.total === 0) {
      setViewMode('all');
    }
  }, [unsurrenderedData]);

  // Mutation for status change
  const setStatusMutation = useMutation({
    mutationFn: ({
      docType,
      systemNumber,
      status,
    }: {
      docId: string;
      docType: 'CONTRACT' | 'AMENDMENT' | 'ACT';
      systemNumber: number;
      status: 'NOT_RETURNED' | 'CLIENT_DELIVERED' | 'CLIENT_RETURNED';
    }) => clientDocumentsApi.setDocumentStatus(clientIdNum, docType, systemNumber, status),
    onMutate: async ({ docId }) => {
      setUpdatingDocs((prev) => new Set(prev).add(docId));
    },
    onSuccess: (_, { status }) => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['client-documents', clientIdNum] });
      queryClient.invalidateQueries({ queryKey: ['client-detail', clientIdNum] });

      const messages: Record<string, string> = {
        NOT_RETURNED: 'Статус сброшен',
        CLIENT_DELIVERED: 'Отмечено: отдал клиенту',
        CLIENT_RETURNED: 'Отмечено: клиент вернул',
      };
      toast.success(messages[status]);
    },
    onError: (error: unknown, { docId }) => {
      console.error('Error updating status:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Ошибка обновления';
      toast.error('Ошибка', { description: errorMessage });
      setUpdatingDocs((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    },
    onSettled: (_, __, { docId }) => {
      setUpdatingDocs((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    },
  });

  const handleStatusChange = (doc: UnsurrenderedDocument, status: 'NOT_RETURNED' | 'CLIENT_DELIVERED' | 'CLIENT_RETURNED') => {
    setStatusMutation.mutate({
      docId: doc.id,
      docType: doc.type,
      systemNumber: doc.systemNumber,
      status,
    });
  };

  // Undo status: CLIENT_RETURNED → CLIENT_DELIVERED → NOT_RETURNED
  const handleUndo = (doc: UnsurrenderedDocument) => {
    if (doc.returnStatus === 'CLIENT_RETURNED') {
      handleStatusChange(doc, 'CLIENT_DELIVERED');
    } else if (doc.returnStatus === 'CLIENT_DELIVERED') {
      handleStatusChange(doc, 'NOT_RETURNED');
    }
  };

  // Preview PDF - show inline viewer (for unsurrendered docs)
  const handlePreview = async (doc: UnsurrenderedDocument) => {
    setPreviewingDoc(doc.id);

    try {
      const blob = await clientDocumentsApi.getDocumentPdf(doc, signerId);
      const url = URL.createObjectURL(blob);
      const title = `${getDocTypeLabel(doc.type)} №${doc.displayNumber}`;
      setPdfViewerData({ url, title });
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error('Ошибка открытия документа');
    } finally {
      setPreviewingDoc(null);
    }
  };

  // Preview PDF for any document type (including INVOICE)
  const handlePreviewAny = async (doc: ClientDocument) => {
    setPreviewingDoc(doc.id);

    try {
      const blob = await clientDocumentsApi.getAnyDocumentPdf(doc, signerId);
      const url = URL.createObjectURL(blob);
      const title = `${getDocTypeLabel(doc.type)} №${doc.displayNumber}`;
      setPdfViewerData({ url, title });
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error('Ошибка открытия документа');
    } finally {
      setPreviewingDoc(null);
    }
  };

  // Close PDF viewer
  const closePdfViewer = () => {
    if (pdfViewerData) {
      URL.revokeObjectURL(pdfViewerData.url);
      setPdfViewerData(null);
    }
  };

  // Computed values
  const unsurrenderedDocuments = unsurrenderedData?.documents || [];
  const summary = unsurrenderedData?.summary;
  const allDocuments = allDocsData?.documents || [];
  const selectedDocuments = unsurrenderedDocuments.filter((d: UnsurrenderedDocument) => selectedDocs.has(d.id));
  const selectedAllDocuments = allDocuments.filter((d: ClientDocument) => selectedDocs.has(d.id));
  const isLoading = viewMode === 'unsurrendered' ? isLoadingUnsurrendered : isLoadingAllDocs;
  const hasUnsurrendered = (summary?.total || 0) > 0;

  // Documents to use for reprint (depends on mode)
  const reprintDocumentNames = useMemo(() => {
    if (viewMode === 'unsurrendered') {
      return selectedDocuments.map((d: UnsurrenderedDocument) =>
        `${getDocTypeLabel(d.type)} №${d.displayNumber} от ${formatShortDate(d.date)}`
      );
    } else {
      return selectedAllDocuments.map((d: ClientDocument) =>
        `${getDocTypeLabel(d.type)} №${d.displayNumber} от ${formatShortDate(d.date)}`
      );
    }
  }, [viewMode, selectedDocuments, selectedAllDocuments]);

  // Handle reprint request
  const handleReprint = (copies: number) => {
    if (!clientIdNum || reprintDocumentNames.length === 0) return;

    const task = `Перепечатать (${copies} копий): ${reprintDocumentNames.join(', ')}`;
    createDocument.mutate(
      { clientId: clientIdNum, task },
      {
        onSuccess: () => {
          setReprintDialogOpen(false);
          setSelectedDocs(new Set());
        },
      }
    );
  };

  // Toggle document selection
  const toggleSelection = (docId: string) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  // Download selected documents
  const handleDownload = async () => {
    const docsToDownload = unsurrenderedDocuments.filter((d: UnsurrenderedDocument) => selectedDocs.has(d.id));
    if (docsToDownload.length === 0) return;

    setIsDownloading(true);
    toast.info(`Скачивание ${docsToDownload.length} документов...`);

    for (const doc of docsToDownload) {
      try {
        const blob = await clientDocumentsApi.getDocumentPdf(doc, signerId);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${getDocTypeLabel(doc.type)}_${doc.displayNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error downloading ${doc.displayNumber}:`, error);
        toast.error(`Ошибка скачивания ${doc.displayNumber}`);
      }
    }

    setIsDownloading(false);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-card">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/visits/client/${clientId}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">
            {viewMode === 'unsurrendered' ? 'Несданные документы' : 'Все документы (3 мес.)'}
          </h1>
          {clientDetail && (
            <p className="text-sm text-muted-foreground truncate">
              {clientDetail.client.name}
            </p>
          )}
        </div>
        {/* Mode switcher */}
        {(hasUnsurrendered || viewMode === 'all') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'unsurrendered' ? 'all' : 'unsurrendered')}
          >
            {viewMode === 'unsurrendered' ? 'Все' : 'Несданные'}
          </Button>
        )}
      </div>

      {/* Summary - only for unsurrendered mode */}
      {viewMode === 'unsurrendered' && summary && (
        <div className="p-4 border-b bg-muted/30">
          <div className="flex items-center gap-4 text-sm">
            <span>Всего: <strong>{summary.total}</strong></span>
            {summary.danger > 0 && (
              <span className="text-amber-600">⚠ Опасных: {summary.danger}</span>
            )}
            {summary.critical > 0 && (
              <span className="text-red-600">🔴 Критических: {summary.critical}</span>
            )}
          </div>
        </div>
      )}

      {/* All docs summary */}
      {viewMode === 'all' && allDocsData && (
        <div className="p-4 border-b bg-muted/30">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span>Всего: <strong>{allDocsData.total}</strong></span>
            {allDocsData.counts.contracts > 0 && (
              <span className="text-muted-foreground">Договоров: {allDocsData.counts.contracts}</span>
            )}
            {allDocsData.counts.amendments > 0 && (
              <span className="text-muted-foreground">Допсогл.: {allDocsData.counts.amendments}</span>
            )}
            {allDocsData.counts.invoices > 0 && (
              <span className="text-muted-foreground">Счетов: {allDocsData.counts.invoices}</span>
            )}
            {allDocsData.counts.acts > 0 && (
              <span className="text-muted-foreground">Актов: {allDocsData.counts.acts}</span>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : viewMode === 'all' ? (
          /* All documents view with history */
          allDocuments.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Нет документов за последние 3 месяца</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {allDocuments.map((doc) => {
                const isPreviewing = previewingDoc === doc.id;
                const isSelected = selectedDocs.has(doc.id);

                return (
                  <Collapsible key={doc.id} className={cn(
                    "border rounded-lg overflow-hidden",
                    isSelected && "ring-2 ring-primary ring-offset-1"
                  )}>
                    <div className="p-3 bg-card">
                      {/* Document header */}
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(doc.id)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">
                              {getDocTypeLabel(doc.type)} №{doc.displayNumber}
                            </div>
                          <div className="text-sm text-muted-foreground">
                            от {formatDate(doc.date)}
                            {doc.amount != null && (
                              <span className="ml-2">{doc.amount.toLocaleString('ru-RU')} BYN</span>
                            )}
                          </div>
                          {/* Payment state for invoices */}
                          {doc.type === 'INVOICE' && doc.paymentState && (
                            <span className={cn(
                              'text-xs font-medium',
                              doc.paymentState === 'PAID' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
                            )}>
                              {doc.paymentState === 'PAID' ? '✓ Оплачен' : 'Не оплачен'}
                            </span>
                          )}
                          {/* Return status */}
                          <div className={cn('text-sm font-medium', getReturnStatusColor(doc.returnStatus))}>
                            {getReturnStatusLabel(doc.returnStatus)}
                          </div>
                        </div>

                          {/* Preview button */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="px-2 shrink-0"
                            disabled={isPreviewing}
                            onClick={() => handlePreviewAny(doc)}
                          >
                            {isPreviewing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* History trigger */}
                      {doc.statusHistory.length > 0 && (
                        <CollapsibleTrigger className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <History className="h-3 w-3" />
                          <span>История ({doc.statusHistory.length})</span>
                          <ChevronDown className="h-3 w-3 transition-transform [[data-state=open]_&]:rotate-180" />
                        </CollapsibleTrigger>
                      )}
                    </div>

                    {/* History content */}
                    <CollapsibleContent className="border-t bg-muted/30">
                      <div className="p-3 space-y-2">
                        {doc.statusHistory.map((entry) => (
                          <div key={entry.id} className="text-xs flex items-start gap-2">
                            <span className="text-muted-foreground whitespace-nowrap">
                              {formatDateTime(entry.recordedAt)}
                            </span>
                            <span className={getReturnStatusColor(entry.returnStatus)}>
                              {getReturnStatusLabel(entry.returnStatus)}
                            </span>
                            {entry.employeeName1C && (
                              <span className="text-muted-foreground">({entry.employeeName1C})</span>
                            )}
                            <span className="text-muted-foreground/60 ml-auto">{entry.source}</span>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )
        ) : unsurrenderedDocuments.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Все документы сданы</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {unsurrenderedDocuments.map((doc) => {
              const isUpdating = updatingDocs.has(doc.id);
              const isDelivered = doc.returnStatus === 'CLIENT_DELIVERED';
              const isReturned = doc.returnStatus === 'CLIENT_RETURNED';
              const isSelected = selectedDocs.has(doc.id);
              const isPreviewing = previewingDoc === doc.id;

              return (
                <div
                  key={doc.id}
                  className={cn(
                    'p-3 border rounded-lg space-y-2',
                    doc.ageZone === 'danger' && 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/50',
                    (doc.ageZone === 'critical' || doc.ageZone === 'extreme') && 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/50',
                    isSelected && 'ring-2 ring-primary ring-offset-1',
                  )}
                >
                  {/* Document info with checkbox */}
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(doc.id)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">
                            {getDocTypeLabel(doc.type)} №{doc.displayNumber}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            от {formatDate(doc.date)}
                          </div>
                          {/* Barcode */}
                          <div className="text-xs text-muted-foreground font-mono mt-0.5">
                            {doc.barcode}
                          </div>
                        </div>
                        <div className={cn('text-sm text-right flex-shrink-0', getAgeZoneStyle(doc.ageZone))}>
                          <div>{doc.ageDays} дн.</div>
                          {doc.ageZone !== 'normal' && (
                            <div className="text-xs">{getAgeZoneLabel(doc.ageZone)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status indicator with undo */}
                  {isDelivered && (
                    <div className="flex items-center gap-1 ml-7">
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">✓ Отдан клиенту</span>
                      <button
                        onClick={() => handleUndo(doc)}
                        disabled={isUpdating}
                        className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                        title="Отменить"
                      >
                        {isUpdating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  )}
                  {isReturned && (
                    <div className="flex items-center gap-1 ml-7">
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Клиент вернул (ожидает подтверждения офиса)</span>
                      <button
                        onClick={() => handleUndo(doc)}
                        disabled={isUpdating}
                        className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                        title="Отменить"
                      >
                        {isUpdating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 ml-7">
                    {/* Preview button - always visible */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="px-2"
                      disabled={isPreviewing}
                      onClick={() => handlePreview(doc)}
                    >
                      {isPreviewing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    {/* Status buttons - hide when already returned */}
                    {!isReturned && (
                      <>
                        {!isDelivered && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            disabled={isUpdating}
                            onClick={() => handleStatusChange(doc, 'CLIENT_DELIVERED')}
                          >
                            {isUpdating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Отдал клиенту'
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={isDelivered ? 'default' : 'outline'}
                          className="flex-1"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(doc, 'CLIENT_RETURNED')}
                        >
                          {isUpdating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Клиент вернул'
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom action bar when documents are selected */}
      {selectedDocs.size > 0 && (
        <div className="p-4 border-t bg-card space-y-2">
          {viewMode === 'unsurrendered' && (
            <div className="flex gap-2">
              <Button
                variant="download"
                className="flex-1"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Скачать ({selectedDocs.size})
              </Button>
              <Button
                variant="email"
                className="flex-1"
                onClick={() => setEmailDialogOpen(true)}
              >
                <Mail className="h-4 w-4" />
                Выслать ({selectedDocs.size})
              </Button>
            </div>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setReprintDialogOpen(true)}
          >
            <Printer className="h-4 w-4" />
            Перепечатать ({selectedDocs.size})
          </Button>
        </div>
      )}

      {/* Email dialog */}
      {legalEntity && (
        <UnsurrenderedEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          clientLegalName={clientDetail?.client.legalName || clientDetail?.client.name || ''}
          legalEntity={legalEntity}
          signerId={signerId}
          documents={selectedDocuments}
        />
      )}

      {/* Reprint dialog */}
      <ReprintDialog
        open={reprintDialogOpen}
        onOpenChange={setReprintDialogOpen}
        documentNames={reprintDocumentNames}
        onConfirm={handleReprint}
        isPending={createDocument.isPending}
      />

      {/* Fullscreen PDF viewer */}
      {pdfViewerData && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 p-3 border-b bg-card shrink-0">
            <Button variant="ghost" size="icon" onClick={closePdfViewer}>
              <X className="h-5 w-5" />
            </Button>
            <span className="font-medium truncate flex-1">{pdfViewerData.title}</span>
          </div>
          {/* PDF embed */}
          <div className="flex-1 overflow-hidden bg-white">
            <embed
              src={pdfViewerData.url}
              type="application/pdf"
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
